import React, { FormEvent, useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom'
import { Formik, Form, FormikHelpers as FormikActions } from 'formik';
import { clone } from 'lodash';

import { PermissionDenied } from 'django-rest-react';
import { get_problema_thread, assegna_problemi } from '../api';
import {
  Problema, LoadingCircle, CategoriaMap,
  FormGroupBootstrap, ErrorBlockGeneric,
  ConfirmedButton,
  CategoriaSelector
} from '../components';
import { Partecipante, Categoria } from '../api/types';
import {
  displayPartecipanteBadge, displayPartecipante, displayCategoria, sentry_log
} from '../utils';
import { canAssegnare, login_url } from '../globals'
import { useCategoria, useLogin, usePartecipante } from '../reducers';

interface FormValues {
  categorie: number[],
  nascondi_chi_vede_gia: boolean,
}

type Thread = Awaited<ReturnType<typeof get_problema_thread>>


const AssegnaProblema = (props: {}) => {
  const login = useLogin();
  const partecipanti_all = usePartecipante(props);
  const categorie = useCategoria(props);

  const [thread, setThread] = useState<Thread | null>(null);
  const [listaFiltrata, setListaFiltrata] = useState<Partecipante[]>([])
  const [error, setError] = useState<string | null>(null)
  const [personeAssegnate, setPersoneAssegnate] = useState(new Set<number>())
  const [order, setOrder] = useState(1 as 1 | -1)
  const navigate = useNavigate()
  const params = useParams<{ pk: string }>()

  function ifPartecipantehasEventoAssegnato(partecipante: Partecipante, thread: Thread) {
    for (const ev of thread.problema.eventi_proposto) {
      if (partecipante.eventi_assegnati.includes(ev)) {
        return true
      }
    }
    return false
  }

  // Nascondi sempre l'autore del problema
  let partecipanti = partecipanti_all.filter(p => !(thread?.autore == p.id));

  const sort = (sortparam: (i: Partecipante) => number | string) => {
    setListaFiltrata(val => {
      const nuovo = Object.assign([], val);
      if (typeof sortparam(nuovo[0]) == "number") {
        nuovo.sort((a, b) => (order * ((sortparam(a) as number) - (sortparam(b) as number))));
      } else {
        const func = (a: Partecipante, b: Partecipante) => {
          let cusu = 1;
          if (sortparam(a) < sortparam(b)) {
            cusu = -1
          }
          return order * cusu;
        }
        nuovo.sort((a, b) => func(a, b));
      }
      return nuovo
    })
    setOrder(val => (val * -1) as 1 | -1)
  }

  const filtra = (values: FormValues, actions: FormikActions<FormValues> | undefined) => {
    let nuovo = partecipanti;
    // thread è sempre impostato, ma il type checker non lo sa
    if (thread && values.nascondi_chi_vede_gia) {
      nuovo = nuovo.filter(p =>
        // Vogliamo sempre vedere quelli cui è già assegnato il problema
        (thread.problema.correttori_assegnati.includes(p.id)) ||
        (
          // Fabio
          !(p.permessi == "a") &&
          // Quelli che hanno l'evento già assegnato
          (!ifPartecipantehasEventoAssegnato(p, thread))
        )
      )
    }
    if (values.categorie.length > 0) {
      for (const c of values.categorie) {
        nuovo = nuovo.filter(item => item.categorie_preferite.includes(c));
      }
    }
    if (actions) {
      actions.setSubmitting(false);
    }
    setListaFiltrata(nuovo);
  }

  const affinita_categorie = (partecipante: Partecipante) => {
    if (!thread) {
      return 0
    }
    return thread.problema.categoria.filter(c => partecipante.categorie_preferite.includes(c)).length;
  }

  const initialValues = {
    categorie: [],
    nascondi_chi_vede_gia: true,
  }


  const pk = parseInt(params.pk as string);

  // Cose da impostare appena è noto il pk del problema
  useEffect(() => {
    document.title = `Assegna #${pk} - Owlitrack`
    get_problema_thread(pk).then(thread => {
      setThread(thread)
      setPersoneAssegnate(new Set(thread.problema.correttori_assegnati))
    });
  }, [pk])

  // Cose da impostare dopo aver settato thread
  useEffect(() => {
    filtra(initialValues, undefined)
    sort(affinita_categorie)
  }, [thread])

  if (!login.logged_in) {
    login_url(window.location.hash);
  }
  if (!canAssegnare(login.user)) {
    throw new PermissionDenied({ message: "Devi essere almeno smistatore per assegnare problemi. " })
  }
  if (!thread) {
    return (
      <LoadingCircle />
    );
  }


  return (
    <div className="container-fluid">
      <h1>Assegna un problema</h1>
      <ErrorBlockGeneric error={error} />
      <Problema thread={thread} />
      <br />
      <h3>Filtra le persone per categoria preferita</h3>
      <Formik
        initialValues={initialValues}
        onSubmit={(values, actions) => { filtra(values, actions); return }}
      >
        {({ isSubmitting, handleReset }) => {
          return (
            <Form>
              <CategoriaSelector
                name="categorie"
                help_text={"Selezioni multiple vengono interpretate come AND. Le categorie selezionate appaiono colorate."}
              />
              <FormGroupBootstrap
                name="nascondi_chi_vede_gia" type="checkbox"
                displayName="Nascondi chi vede"
                help_text="Nascondi nella lista i partecipanti che vedono già il problema."
              />
              <div className="d-flex justify-content-end">
                <ConfirmedButton
                  type="warning"
                  onSubmit={handleReset}>
                  Reset
                </ConfirmedButton>
                <button type="submit" disabled={isSubmitting}
                  className="mx-1 btn btn-primary">Filtra</button>
              </div>
            </Form>
          );
        }}
      </Formik>
      <br />
      <div className="table-responsive">
        <table className="table table-bordered table-hover table-striped">
          <thead className="thead-dark">
            <tr>
              <th onClick={() => sort((item) => displayPartecipante(item.id))}>
                <i className="fa fa-sort" /> Nome</th>
              <th onClick={() => sort((item) => item.quanti_assegnati)}>
                <i className="fa fa-sort" /> Assegnati</th>
              <th onClick={() => sort((item) => item.quanti_corretti)}>
                <i className="fa fa-sort" /> Risolti</th>
              <th onClick={() => sort((item) => (item.quanti_assegnati - item.quanti_assegnati_fatti))}>
                <i className="fa fa-sort" />Da fare</th>
              <th onClick={() => sort(affinita_categorie)}>
                <i className="fa fa-sort" />Categorie</th>
              <th>Assegna</th>
            </tr>
          </thead>
          <tbody>
            {listaFiltrata.map(item => {
              const old_checked = personeAssegnate.has(item.id)
              return (
                <tr key={item.id}>
                  <td>{displayPartecipanteBadge(item)}</td>
                  <td>{item.quanti_assegnati}</td>
                  <td>{item.quanti_corretti}</td>
                  <td>{item.quanti_assegnati - item.quanti_assegnati_fatti}</td>
                  <td><CategoriaMap categorie={item.categorie_preferite} /></td>
                  <td>
                    <input
                      type="checkbox" checked={old_checked}
                      onChange={(e) => {
                        const { checked } = e.target;
                        const nuovi_assegnati = clone(personeAssegnate);
                        if (checked) {
                          nuovi_assegnati.add(item.id);
                        } else {
                          nuovi_assegnati.delete(item.id);
                        }
                        setPersoneAssegnate(nuovi_assegnati)
                      }}
                    />
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>
      <div className="d-flex justify-content-end">
        <button type="reset" onClick={() => setPersoneAssegnate(new Set(thread.problema.correttori_assegnati))}
          className="mx-1 btn btn-warning">Reset</button>
        <button type="submit" onClick={(e: FormEvent) => {
          e.preventDefault()
          const submitting_data = {
            correttori_assegnati: Array.from(personeAssegnate),
          }
          assegna_problemi(thread.problema.id, submitting_data)
            .then(() => navigate(`/thread/problema/${thread.id}/`))
            .catch(error => {
              sentry_log(error);
              setError(error);
            });
        }}
          className="mx-1 btn btn-primary">Invia</button>
      </div>
    </div >
  );
}


export default AssegnaProblema;
